import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-background-medium);
  &:hover {
    cursor: pointer;
  }

  ${({ active }) =>
    active &&
    css`
      background: var(--color-background-darker);
    `}
`;

const ScrollIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;

  div {
    margin-right: 12px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const getScrollData = (element) => {
  const windowScroll = element.scrollLeft;
  const totalWidth = element.scrollWidth - element.clientWidth;
  return [windowScroll, totalWidth];
};

const DotsScrollbar = ({ amount, target }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const scrollListener = useCallback(() => {
    if (!target.current) {
      return;
    }

    const [windowScroll, totalWidth] = getScrollData(target.current);

    if (windowScroll === 0) {
      setScrollProgress(0);
    } else if (windowScroll > totalWidth) {
      setScrollProgress(100);
    } else {
      setScrollProgress((windowScroll / totalWidth) * 100);
    }
  }, [target]);

  useEffect(() => {
    const node = target.current;
    node.addEventListener('scroll', scrollListener);
    return () => node.removeEventListener('scroll', scrollListener);
  }, [target, scrollListener]);

  const handleClickDot = (order) => {
    const [, totalWidth] = getScrollData(target.current);
    const sizeForEachDot = totalWidth / amount;

    target.current.scroll({
      left: sizeForEachDot * order + sizeForEachDot / 2,
      behavior: 'smooth',
    });
  };

  const renderDots = () => {
    const selectedDotValue = (scrollProgress * amount) / 100;
    return [...Array(amount).keys()].map((index) => (
      <Dot
        key={index}
        active={selectedDotValue >= index && selectedDotValue <= index + 1}
        onClick={() => handleClickDot(index)}
      />
    ));
  };

  return <ScrollIndicatorContainer>{renderDots()}</ScrollIndicatorContainer>;
};

export default DotsScrollbar;
